import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useAuth } from '@/lib/auth';
import { setLocale as setFormatLocale, type SupportedLocale } from '@/lib/locale';

const MESSAGES = {
  en: {
    'common.signOut': 'Sign out', 'common.signedIn': 'Signed in',
    'common.openMenu': 'Open the menu', 'theme.system': 'System', 'theme.light': 'Light',
    'theme.dark': 'Dark', 'theme.switch': 'Theme: {current}. Switch to {next}',
    'language.label': 'Language', 'language.current': 'Language: {current}',
  },
  'zh-CN': {
    'common.signOut': '退出登录', 'common.signedIn': '已登录',
    'common.openMenu': '打开菜单', 'theme.system': '跟随系统', 'theme.light': '浅色',
    'theme.dark': '深色', 'theme.switch': '主题：{current}。切换至{next}',
    'language.label': '语言', 'language.current': '语言：{current}',
  },
  vi: {
    'common.signOut': 'Đăng xuất', 'common.signedIn': 'Đã đăng nhập',
    'common.openMenu': 'Mở menu', 'theme.system': 'Hệ thống', 'theme.light': 'Sáng',
    'theme.dark': 'Tối', 'theme.switch': 'Giao diện: {current}. Chuyển sang {next}',
    'language.label': 'Ngôn ngữ', 'language.current': 'Ngôn ngữ: {current}',
  },
} as const;

type MessageKey = keyof typeof MESSAGES.en;
type Values = Record<string, string | number>;

const SOURCE_MESSAGES: Record<string, Record<Exclude<SupportedLocale, 'en'>, string>> = {
  'Production Monthly Output': { 'zh-CN': '月度生产产量', vi: 'Sản lượng sản xuất theo tháng' },
  '2nd Operation': { 'zh-CN': '二次工序', vi: 'Công đoạn 2' },
  'Sewing Efficiency': { 'zh-CN': '车缝效率', vi: 'Hiệu suất may' },
  'Sewing Factory Board': { 'zh-CN': '车缝工厂看板', vi: 'Bảng điều hành may cấp nhà máy' },
  'Sewing Floor Board': { 'zh-CN': '车缝楼层看板', vi: 'Bảng điều hành may cấp tầng' },
  'Sewing Hourly Board': { 'zh-CN': '车缝小时看板', vi: 'Bảng điều hành may theo giờ' },
  Spreading: { 'zh-CN': '铺布', vi: 'Trải vải' },
  'T2 Business Report': { 'zh-CN': 'T2 业务报表', vi: 'Báo cáo kinh doanh T2' },
  'T2 Business Dashboard': { 'zh-CN': 'T2 业务看板', vi: 'Bảng điều hành kinh doanh T2' },
  'Order Management': { 'zh-CN': '订单管理', vi: 'Quản lý đơn hàng' },
  'Material Planning': { 'zh-CN': '物料计划', vi: 'Kế hoạch vật liệu' },
  'Raw Material': { 'zh-CN': '原材料', vi: 'Nguyên vật liệu' },
  'Production Control': { 'zh-CN': '生产控制', vi: 'Kiểm soát sản xuất' },
  'Quality Assurance': { 'zh-CN': '质量保证', vi: 'Đảm bảo chất lượng' },
  'Delivery Performance': { 'zh-CN': '交付绩效', vi: 'Hiệu quả giao hàng' },
  'HR & Admin': { 'zh-CN': '人力与行政', vi: 'Nhân sự & Hành chính' },
  Cutting: { 'zh-CN': '裁剪', vi: 'Cắt' },
  SOP: { 'zh-CN': 'SOP', vi: 'SOP' },
  Supermarket: { 'zh-CN': '超市', vi: 'Siêu thị' },
  Sewing: { 'zh-CN': '车缝', vi: 'May' },
  'Finished Goods': { 'zh-CN': '成品', vi: 'Thành phẩm' },
  Attendance: { 'zh-CN': '考勤', vi: 'Chấm công' },
  All: { 'zh-CN': '全部', vi: 'Tất cả' },
  Clear: { 'zh-CN': '清除', vi: 'Xóa' },
  Factory: { 'zh-CN': '工厂', vi: 'Nhà máy' },
  Date: { 'zh-CN': '日期', vi: 'Ngày' },
  'Date range': { 'zh-CN': '日期范围', vi: 'Khoảng ngày' },
  From: { 'zh-CN': '从', vi: 'Từ' },
  To: { 'zh-CN': '至', vi: 'Đến' },
  Brand: { 'zh-CN': '品牌', vi: 'Nhãn hàng' },
  Style: { 'zh-CN': '款式', vi: 'Mã hàng' },
  Order: { 'zh-CN': '订单', vi: 'Đơn hàng' },
  Floor: { 'zh-CN': '楼层', vi: 'Tầng' },
  Line: { 'zh-CN': '生产线', vi: 'Chuyền' },
  'Type to filter': { 'zh-CN': '输入内容进行筛选', vi: 'Nhập để lọc' },
  '{count} selected': { 'zh-CN': '已选 {count} 项', vi: 'Đã chọn {count}' },
  'Search {field}': { 'zh-CN': '搜索{field}', vi: 'Tìm {field}' },
  'Nothing matches “{term}”. Try a shorter search.': {
    'zh-CN': '没有与“{term}”匹配的结果。请缩短搜索词。',
    vi: 'Không có kết quả khớp “{term}”. Hãy thử từ khóa ngắn hơn.',
  },
  'Sign in': { 'zh-CN': '登录', vi: 'Đăng nhập' },
  'Signing in…': { 'zh-CN': '正在登录…', vi: 'Đang đăng nhập…' },
  Username: { 'zh-CN': '用户名', vi: 'Tên đăng nhập' },
  Password: { 'zh-CN': '密码', vi: 'Mật khẩu' },
  'Reports, by area': { 'zh-CN': '按业务区域查看报表', vi: 'Báo cáo theo khu vực' },
  Overview: { 'zh-CN': '概览', vi: 'Tổng quan' },
  'Welcome, {name}': { 'zh-CN': '欢迎，{name}', vi: 'Chào mừng, {name}' },
  Reports: { 'zh-CN': '报表', vi: 'Báo cáo' },
  'Filter reports': { 'zh-CN': '筛选报表', vi: 'Lọc báo cáo' },
  'Open reports': { 'zh-CN': '已打开的报表', vi: 'Báo cáo đang mở' },
  'Expand the menu': { 'zh-CN': '展开菜单', vi: 'Mở rộng menu' },
  'Collapse the menu': { 'zh-CN': '收起菜单', vi: 'Thu gọn menu' },
  'Expand menu': { 'zh-CN': '展开菜单', vi: 'Mở rộng menu' },
  'Collapse menu': { 'zh-CN': '收起菜单', vi: 'Thu gọn menu' },
  'Skip to the report': { 'zh-CN': '跳到报表', vi: 'Đi đến báo cáo' },
  Filters: { 'zh-CN': '筛选条件', vi: 'Bộ lọc' },
  'Clear filters': { 'zh-CN': '清除筛选', vi: 'Xóa bộ lọc' },
  Query: { 'zh-CN': '查询', vi: 'Truy vấn' },
  Export: { 'zh-CN': '导出', vi: 'Xuất' },
  'Exporting…': { 'zh-CN': '正在导出…', vi: 'Đang xuất…' },
  'Export to Excel': { 'zh-CN': '导出到 Excel', vi: 'Xuất sang Excel' },
  'Export to CSV': { 'zh-CN': '导出到 CSV', vi: 'Xuất sang CSV' },
  'The export failed. Try again, or narrow the filters.': {
    'zh-CN': '导出失败。请重试或缩小筛选范围。',
    vi: 'Xuất dữ liệu thất bại. Hãy thử lại hoặc thu hẹp bộ lọc.',
  },
  'Data as of': { 'zh-CN': '数据截至', vi: 'Dữ liệu đến' },
  'This view ran at': { 'zh-CN': '此视图运行于', vi: 'Chế độ xem chạy lúc' },
  'No reports yet.': { 'zh-CN': '暂无报表。', vi: 'Chưa có báo cáo.' },
  'Go to overview': { 'zh-CN': '前往概览', vi: 'Đi đến tổng quan' },
  'There is no page at this address': { 'zh-CN': '此地址没有页面', vi: 'Không có trang tại địa chỉ này' },
  'Your roles don’t cover this report': {
    'zh-CN': '您的角色无权访问此报表',
    vi: 'Vai trò của bạn không có quyền xem báo cáo này',
  },
  'A new version of WinSight is ready. Reload when it suits you — your filters are in the address bar, so nothing is lost.': {
    'zh-CN': 'WinSight 新版本已就绪。您可随时重新加载；筛选条件保存在地址栏中，不会丢失。',
    vi: 'Phiên bản WinSight mới đã sẵn sàng. Hãy tải lại khi thuận tiện; bộ lọc nằm trên thanh địa chỉ nên không bị mất.',
  },
  Reload: { 'zh-CN': '重新加载', vi: 'Tải lại' },
  'Dismiss the update notice': { 'zh-CN': '关闭更新通知', vi: 'Đóng thông báo cập nhật' },
  'Leave full screen': { 'zh-CN': '退出全屏', vi: 'Thoát toàn màn hình' },
  'Show full screen': { 'zh-CN': '全屏显示', vi: 'Hiển thị toàn màn hình' },
  'This report returned more rows than can be shown, so the list below is incomplete and any figure read off it covers part of the period only.': {
    'zh-CN': '此报表返回的行数超过可显示范围，因此下方列表不完整，列表中的数字仅覆盖部分期间。',
    vi: 'Báo cáo trả về nhiều dòng hơn giới hạn hiển thị, vì vậy danh sách bên dưới chưa đầy đủ và các số liệu chỉ bao quát một phần kỳ báo cáo.',
  },
  'Narrow the dates or add a filter to see the whole result': {
    'zh-CN': '缩小日期范围或添加筛选条件以查看完整结果',
    vi: 'Thu hẹp khoảng ngày hoặc thêm bộ lọc để xem toàn bộ kết quả',
  },
  'Table pagination': { 'zh-CN': '表格分页', vi: 'Phân trang bảng' },
  'Previous page': { 'zh-CN': '上一页', vi: 'Trang trước' },
  'Next page': { 'zh-CN': '下一页', vi: 'Trang sau' },
  'Rows per page': { 'zh-CN': '每页行数', vi: 'Số dòng mỗi trang' },
  'Page {current} of {total}': { 'zh-CN': '第 {current} 页，共 {total} 页', vi: 'Trang {current} / {total}' },
  '{first}–{last} of {total}': { 'zh-CN': '{first}–{last}，共 {total}', vi: '{first}–{last} trên {total}' },
  'No rows match these filters. Widen the date range or clear a filter to see more.': {
    'zh-CN': '没有符合筛选条件的行。请扩大日期范围或清除筛选条件。',
    vi: 'Không có dòng nào khớp bộ lọc. Hãy mở rộng khoảng ngày hoặc xóa bộ lọc.',
  },
  'Open another {report}': { 'zh-CN': '另开一个 {report}', vi: 'Mở thêm {report}' },
  'Close {report}': { 'zh-CN': '关闭 {report}', vi: 'Đóng {report}' },
  'Sort by {column}': { 'zh-CN': '按 {column} 排序', vi: 'Sắp xếp theo {column}' },
  'WinSight could not be reached': { 'zh-CN': '无法连接 WinSight', vi: 'Không thể kết nối WinSight' },
  'Check your connection, then reload the page.': {
    'zh-CN': '请检查网络连接，然后重新加载页面。',
    vi: 'Hãy kiểm tra kết nối rồi tải lại trang.',
  },
  'Your session has ended': { 'zh-CN': '您的会话已结束', vi: 'Phiên đăng nhập đã kết thúc' },
  'This report is not shared with your role': {
    'zh-CN': '此报表未向您的角色开放',
    vi: 'Báo cáo này chưa được chia sẻ với vai trò của bạn',
  },
  'This report no longer exists': { 'zh-CN': '此报表已不存在', vi: 'Báo cáo này không còn tồn tại' },
  'WinSight is temporarily unavailable': { 'zh-CN': 'WinSight 暂时不可用', vi: 'WinSight tạm thời không khả dụng' },
  'The report could not be produced': { 'zh-CN': '无法生成报表', vi: 'Không thể tạo báo cáo' },
  Reference: { 'zh-CN': '参考编号', vi: 'Mã tham chiếu' },
};

interface I18nValue {
  locale: SupportedLocale;
  selectLocale: (locale: SupportedLocale) => void;
  t: (key: MessageKey, values?: Values) => string;
  text: (english: string, values?: Values) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(message: string, values: Values | undefined): string {
  if (values === undefined) return message;
  return message.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? `{${key}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [selection, setSelection] = useState<{
    userId: string;
    locale: SupportedLocale;
  } | null>(null);
  const locale = selection !== null && selection.userId === user?.userId
    ? selection.locale
    : (user?.locale ?? 'en');

  useEffect(() => {
    setFormatLocale(locale);
    document.documentElement.lang = locale === 'zh-CN' ? 'zh-Hans' : locale;
  }, [locale]);
  const value = useMemo<I18nValue>(() => ({
    locale,
    selectLocale: (selected) => {
      if (user !== null) setSelection({ userId: user.userId, locale: selected });
    },
    t: (key, values) => interpolate(MESSAGES[locale][key], values),
    text: (english, values) => interpolate(
      locale === 'en' ? english : (SOURCE_MESSAGES[english]?.[locale] ?? english),
      values,
    ),
  }), [locale, user]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) throw new Error('useI18n was called outside <I18nProvider>.');
  return value;
}
